import { supabase } from './supabase';
import { WellbeingData } from '../types/wellbeing';

export type WellbeingEntry = WellbeingData & { id: string; createdAt: string };

// Temperatura musi być 35-42°C (constraint w DB). Pusta/0/poza zakresem = null.
const sanitizeTemperature = (data: Partial<WellbeingData>): number | null => {
  if (!data.fever) return null;
  const v = data.temperature;
  if (v == null || v === 0) return null;
  const n = Number(v);
  return n >= 35 && n <= 42 ? n : null;
};

const toSnakeCase = (data: Partial<WellbeingData>) => ({
  date: data.date,
  mood: data.mood,
  stress: data.stress,
  stress_note: data.stressNote,
  energy: data.energy,
  sleep_hours: data.sleepHours,
  sleep_quality: data.sleepQuality,
  abdominal_pain: data.abdominalPain,
  bloating: data.bloating,
  rumbling: data.rumbling,
  heartburn: data.heartburn,
  nausea: data.nausea,
  vomiting: data.vomiting,
  loss_of_appetite: data.lossOfAppetite,
  fullness: data.fullness,
  joint_pain: data.jointPain,
  erythema_nodosum: data.erythemaNodosum,
  pyoderma_gangrenosum: data.pyodermaGangrenosum,
  skin_other: data.skinOther,
  skin_other_note: data.skinOtherNote,
  eye_problems: data.eyeProblems,
  mouth_sores: data.mouthSores,
  diet_change: data.dietChange,
  diet_change_note: data.dietChangeNote,
  meal_heaviness: data.mealHeaviness,
  alcohol: data.alcohol,
  alcohol_note: data.alcoholNote,
  physical_activity: data.physicalActivity,
  weight: data.weight,
  fever: data.fever,
  temperature: sanitizeTemperature(data),
  notes: data.notes,
});

const toCamelCase = (data: any): WellbeingEntry => ({
  id: data.id,
  date: data.date,
  createdAt: data.created_at,
  mood: data.mood,
  stress: data.stress,
  stressNote: data.stress_note,
  energy: data.energy,
  sleepHours: data.sleep_hours,
  sleepQuality: data.sleep_quality,
  abdominalPain: data.abdominal_pain,
  bloating: data.bloating,
  rumbling: data.rumbling,
  heartburn: data.heartburn,
  nausea: data.nausea,
  vomiting: data.vomiting,
  lossOfAppetite: data.loss_of_appetite,
  fullness: data.fullness,
  jointPain: data.joint_pain,
  erythemaNodosum: data.erythema_nodosum,
  pyodermaGangrenosum: data.pyoderma_gangrenosum,
  skinOther: data.skin_other,
  skinOtherNote: data.skin_other_note,
  eyeProblems: data.eye_problems,
  mouthSores: data.mouth_sores,
  dietChange: data.diet_change,
  dietChangeNote: data.diet_change_note,
  mealHeaviness: data.meal_heaviness,
  alcohol: data.alcohol,
  alcoholNote: data.alcohol_note,
  physicalActivity: data.physical_activity,
  weight: data.weight,
  fever: data.fever,
  temperature: data.temperature,
  notes: data.notes,
});

export const saveWellbeingEntry = async (data: WellbeingData): Promise<WellbeingEntry> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('Zaloguj się, aby zapisać wpis');
  }

  if (!data.date) {
    throw new Error('Brak daty wpisu');
  }

  const raw = toSnakeCase(data);
  const payload: Record<string, unknown> = {
    date: data.date,
  };
  for (const [key, value] of Object.entries(raw)) {
    if (value !== undefined && value !== null && key !== 'date') {
      payload[key] = value;
    }
  }

  const existing = await getWellbeingEntryByDate(data.date);

  if (existing) {
    return updateWellbeingEntry(existing.id, data);
  }

  const insertPayload = { user_id: user.id, ...payload };

  const { data: entry, error } = await supabase
    .from('wellbeing_entries')
    .insert(insertPayload)
    .select()
    .single();

  if (error) {
    console.error('Wellbeing save error:', error);
    const msg = error.message || error.code || 'Nie udało się zapisać';
    throw new Error(msg);
  }

  return toCamelCase(entry);
};

export const getWellbeingEntries = async (): Promise<WellbeingEntry[]> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('wellbeing_entries')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map(toCamelCase);
};

export const getWellbeingEntryByDate = async (date: string): Promise<WellbeingEntry | null> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('wellbeing_entries')
    .select('*')
    .eq('user_id', user.id)
    .eq('date', date)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;

  return data ? toCamelCase(data) : null;
};

export const updateWellbeingEntry = async (id: string, data: Partial<WellbeingData>): Promise<WellbeingEntry> => {
  const raw = toSnakeCase(data);
  const payload: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v !== undefined) payload[k] = v;
  }

  const { data: entry, error } = await supabase
    .from('wellbeing_entries')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  return toCamelCase(entry);
};

export const getWellbeingEntry = async (id: string): Promise<WellbeingEntry> => {
  const { data, error } = await supabase
    .from('wellbeing_entries')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;

  return toCamelCase(data);
};

export const deleteWellbeingEntry = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('wellbeing_entries')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

/** Pobiera wpisy samopoczucia w zadanym zakresie dat */
export const getWellbeingEntriesInRange = async (
  userId: string,
  dateFrom: string,
  dateTo: string
): Promise<WellbeingEntry[]> => {
  const { data, error } = await supabase
    .from('wellbeing_entries')
    .select('*')
    .eq('user_id', userId)
    .gte('date', dateFrom)
    .lte('date', dateTo)
    .order('date', { ascending: true });

  if (error) throw error;
  return (data || []).map(toCamelCase);
};

export const getRecentWellbeingEntries = async (days: number = 7): Promise<WellbeingEntry[]> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const { data, error } = await supabase
    .from('wellbeing_entries')
    .select('*')
    .eq('user_id', user.id)
    .gte('date', startDate.toISOString().split('T')[0])
    .order('date', { ascending: false });

  if (error) throw error;

  return (data || []).map(toCamelCase);
};
